/**
 * Field selection for the Data API.
 *
 * Every Data API method sends a generated, depth-bounded full selection by default, which
 * is what makes the first call short. That default is also the whole result: a caller who
 * wanted two fields of a route still receives the 3D geometry, every maneuver, and one
 * elevation float per coordinate. `select` is the way to ask for less, and it is the reason the
 * Data API is GraphQL rather than a REST facade.
 *
 * ```ts
 * const routes = await mountaya.data.directions(input, {
 *   select: { routes: { distance: true, duration: true } },
 * });
 * //    ^? { routes: { distance: number; duration: number }[] }
 * ```
 *
 * The narrowing is real in both directions: the document sent carries only those fields,
 * and the type carries only those fields, so reading `routes[0].geometry` afterwards is a
 * compile error rather than a runtime `undefined`.
 *
 * **The shape a caller may select is derived from the generated tree, not from the result
 * type.** Those look interchangeable and are not: `geometry` is typed as a GeoJSON object,
 * so a `Selection` derived from the result type would happily offer to recurse into
 * `coordinates` and `type`, fields the GraphQL scalar has no sub-selection for and the
 * server would reject. The tree knows what is a leaf; the result type only knows what is
 * an object. Deriving from the tree makes leaf-ness identical at the type level and at
 * runtime, from one source.
 */

import { describeType, MountayaError } from "./errors.js";
import type { RequestOptions } from "./transport.js";

/** One level of indentation in a printed document. Matches the generator's. */
const INDENT = "  ";

/**
 * FieldTree is a generated selection tree: a leaf is `true`, a composite is a nested
 * tree. Key order is the SDL's field order, and it is load-bearing: see buildDocument.
 */
export interface FieldTree {
  readonly [field: string]: true | FieldTree;
}

/**
 * Selection is what a caller may pass as `select` for a given operation's tree.
 *
 * Every field is optional; a composite may be `true` to take its whole subtree, or a
 * nested selection to go further. The check is `extends true` rather than
 * `extends FieldTree` so it never depends on a nested `as const` literal being assignable
 * to an index signature.
 */
export type Selection<Tree> = {
  readonly [K in keyof Tree]?: Tree[K] extends true ? true : true | Selection<Tree[K]>;
};

/**
 * Selected narrows a result type to the fields a selection asked for, unwrapping and
 * re-wrapping arrays and nulls at each level so `{ routes: { distance: true } }` against
 * `{ routes: Route[] }` yields `{ routes: { distance: number }[] }`.
 *
 * The object branch remaps `keyof T` rather than intersecting the two key sets, because a
 * remapped mapping over `keyof T` is homomorphic: it preserves `?` and `readonly` from the
 * result type, which an intersection would silently drop.
 */
export type Selected<T, S> = T extends null | undefined
  ? T
  : T extends readonly (infer E)[]
    ? T extends unknown[]
      ? Selected<E, S>[]
      : readonly Selected<E, S>[]
    : {
        [K in keyof T as K extends keyof S ? K : never]: Exclude<
          S[K & keyof S],
          undefined
        > extends true
          ? T[K]
          : Selected<T[K], S[K & keyof S]>;
      };

/**
 * DataOptions is the second argument of every Data API method.
 *
 * `signal` rides in the bag rather than as a positional parameter, and so does everything
 * after it: a surface that grows a positional argument every time an option arrives is one
 * where the third caller has to remember the order of the first two.
 *
 * It extends the options every REST call takes, so `signal` and the per-call `retry` mean the
 * same thing on all three surfaces, and a knob added there reaches this one too. `select` and
 * the per-call `timeoutMs` are the Data API's own.
 */
export interface DataOptions<S> extends RequestOptions {
  /** The fields to return. Omit it to receive the full result. */
  readonly select?: S | undefined;
  /**
   * How long each attempt of this call waits for a response, in whole milliseconds from 1 to
   * 2,147,483,647, in place of the client's `timeoutMs` and of the Data API's own budget: a slow
   * matrix or isochrone call can wait longer, and a search box less, on one client. Refused
   * before the first attempt when outside the range.
   *
   * @default the larger of the client's own `timeoutMs` and the 30 seconds the Data API spends on
   *   any operation before it answers a 504 itself, plus five seconds: 35 seconds on a client that
   *   keeps its default.
   */
  readonly timeoutMs?: number | undefined;
}

/**
 * DataResult is a method's return type: the full result when nothing was selected, and
 * the narrowed one when something was. The `[S] extends [undefined]` form is deliberate:
 * a naked check would distribute over a union and answer for each member separately.
 */
export type DataResult<T, S> = [S] extends [undefined] ? T : Selected<T, S>;

/**
 * SelectionValue is the runtime shape of a selection, once the types are erased.
 *
 * Exported for the caller that builds a selection dynamically (from a config file, a
 * query string, or a model's answer), where the per-operation `Selection` type has nothing
 * to check against. Such a selection is validated when the document is printed rather than
 * by the compiler, which is why the errors name the valid fields.
 */
export type SelectionValue = { readonly [field: string]: true | SelectionValue | undefined };

/**
 * MountayaSelectionError is a selection that names something the schema does not have.
 *
 * It is thrown before the request leaves, not after the server refuses, so the message can
 * name the fields that do exist at that level, which is what turns a typo into a fix
 * rather than a round trip and a GraphQL validation error.
 */
export class MountayaSelectionError extends MountayaError {
  override readonly name = "MountayaSelectionError";
}

/**
 * The most distinct selections whose documents are kept for one tree.
 *
 * A generated tree answers a closed set of selections in any real application, and would need
 * no bound. A selection built at runtime (from a config file, a query string, a model's answer)
 * opens it, so the cap turns a map that only grows into a warm cache that occasionally misses.
 */
const MAX_MEMOISED_DOCUMENTS = 256;

/**
 * Printed documents, memoised by the CONTENT of the selection that produced them, per tree.
 *
 * Printing walks the tree and concatenates a string per field, so a narrowed call pays it on
 * every call, and the calls that narrow are the ones made in a loop: a list re-fetching on each
 * keystroke, a map refreshing per camera move, a batch walking a thousand rows. The document
 * those produce is identical every time, because it is a pure function of the operation, the
 * tree and the selection.
 *
 * Keyed by content rather than by the selection object: `readonly` stops a caller writing to a
 * selection it typed, and nothing more, so a selection built from a config object or edited
 * through a mutable reference changes under an identity key and the memo answers with the
 * document printed before the edit. The key is {@link selectionKey}, a walk of the members the
 * selection names, which is far shorter than the print's walk of the tree at every level it
 * selects, and a selection written inline at the call site hits as a hoisted one does.
 *
 * Weak by tree, so a tree built at runtime takes its documents with it, and the tree is part of
 * the key at all because the document depends on it: two trees answering to one operation name
 * must not answer with each other's document. The inner key carries the operation and the input
 * type, which the document spells as well.
 */
const documents = new WeakMap<FieldTree, Map<string, string>>();

/**
 * selectionKey spells a selection's content as a string, walking it along `tree` as the printer
 * does, or answers undefined for a selection the memo does not hold.
 *
 * It reads what the printer reads: each own member, `true` or a nested selection, and skips a
 * member that is `undefined`. It descends only where the tree has a composite, so the tree's depth
 * bounds it, as it bounds the printer: a selection that holds itself, or one nested deeper than the
 * tree, ends the walk where the tree ends rather than at the stack's limit. Anything the tree does
 * not answer (a member it lacks, a nested selection on a leaf, a value other than `true` or an
 * object, a prototype other than a plain object's) is not held, so a selection the printer may read
 * differently from this walk is printed every time rather than answered from a key that does not
 * describe it. The printer then refuses what it cannot print, as it would have anyway.
 */
const selectionKey = (selection: object, tree: FieldTree): string | undefined => {
  const prototype: unknown = Object.getPrototypeOf(selection);
  if (prototype !== Object.prototype && prototype !== null) {
    return undefined;
  }

  let key = "{";
  for (const [name, picked] of Object.entries(selection)) {
    if (picked === undefined) {
      continue;
    }

    const node = Object.hasOwn(tree, name) ? tree[name] : undefined;
    if (node === undefined) {
      return undefined;
    }

    if (picked === true) {
      key += `${JSON.stringify(name)}:true,`;

      continue;
    }

    if (node === true || typeof picked !== "object" || picked === null) {
      return undefined;
    }

    const nested = selectionKey(picked, node);
    if (nested === undefined) {
      return undefined;
    }

    key += `${JSON.stringify(name)}:${nested},`;
  }

  return `${key}}`;
};

/**
 * documentFor picks the document an operation should send.
 *
 * Without a selection it returns the generated full-selection string verbatim rather than
 * reprinting it: that is the path every un-narrowed call takes, and returning the
 * generated bytes means the default cannot drift from what the catalog committed. With one, it
 * prints the narrowed document, or answers it from {@link documents} when the same content was
 * printed for the same tree before.
 */
export const documentFor = (
  operation: string,
  inputType: string,
  tree: FieldTree,
  fullDocument: string,
  selection: unknown,
): string => {
  if (selection === undefined) {
    return fullDocument;
  }

  // A selection that is not an object is off-contract and has no content to key on. It goes
  // straight to the printer, which is what names the fields that DO exist at that level: the
  // memo must not turn a diagnosable mistake into a different error.
  if (typeof selection !== "object" || selection === null) {
    return printDocument(operation, inputType, tree, selection);
  }

  const content = selectionKey(selection, tree);
  if (content === undefined) {
    return printDocument(operation, inputType, tree, selection);
  }

  const key = `${operation}\u0000${inputType}\u0000${content}`;
  const memo = documents.get(tree) ?? new Map<string, string>();
  const memoised = memo.get(key);
  if (memoised !== undefined) {
    return memoised;
  }

  // Printed before anything is stored, so a selection that names a field the schema does not
  // have throws on every call rather than only the first. A memo that cached the failure
  // would be a memo of nothing; one that cached nothing and reported success once would be
  // worse.
  const document = printDocument(operation, inputType, tree, selection);

  if (memo.size >= MAX_MEMOISED_DOCUMENTS) {
    // A Map iterates in insertion order, so this is the least recently added entry.
    const oldest = memo.keys().next().value;
    if (oldest !== undefined) {
      memo.delete(oldest);
    }
  }

  memo.set(key, document);
  documents.set(tree, memo);

  return document;
};

/**
 * buildDocument prints a narrowed document for an operation.
 *
 * Asked for every field, the output is byte-identical to the generated full selection, which
 * `data/selection.test.ts` asserts by rebuilding every full selection and comparing it to the
 * generated string. Both walk the tree, so field order comes from the SDL and never from
 * the order the caller listed its fields: two callers selecting the same fields send the
 * same bytes, and therefore share one automatic-persisted-query entry on the Data API.
 */
export const buildDocument = (
  operation: string,
  inputType: string,
  tree: FieldTree,
  selection: SelectionValue,
): string => printDocument(operation, inputType, tree, selection);

/**
 * printDocument is {@link buildDocument} over a selection as it arrived, which a caller outside the
 * compiler can have built as anything: it is checked level by level as it is printed.
 */
const printDocument = (
  operation: string,
  inputType: string,
  tree: FieldTree,
  selection: unknown,
): string => {
  const body = printSelection(operation, tree, selection, 1);
  if (body === "") {
    throw new MountayaSelectionError(`${operation}: a selection must name at least one field`);
  }

  return `query ${exportName(operation)}($input: ${inputType}) {\n${INDENT}${operation}(input: $input) {\n${body}${INDENT}}\n}\n`;
};

/**
 * printSelection renders the body of one selection set. `selection` undefined means take
 * everything below this point, which is both the default document and what `true` on a
 * composite expands to.
 */
const printSelection = (at: string, tree: FieldTree, selection: unknown, depth: number): string => {
  if (selection !== undefined) {
    assertSelectable(at, tree, selection);
  }

  const indent = INDENT.repeat(depth + 1);

  let out = "";
  for (const [name, node] of Object.entries(tree)) {
    const picked = selection === undefined ? true : selection[name];
    if (picked === undefined) {
      continue;
    }

    if (node === true) {
      out += `${indent}${name}\n`;

      continue;
    }

    const sub = printSelection(
      `${at}.${name}`,
      node,
      picked === true ? undefined : picked,
      depth + 1,
    );
    if (sub === "") {
      throw new MountayaSelectionError(`${at}.${name}: a selection must name at least one field`);
    }

    out += `${indent}${name} {\n${sub}${indent}}\n`;
  }

  return out;
};

/** isSelectionValue reports whether a value is a selection: an object that is not an array. */
const isSelectionValue = (value: unknown): value is SelectionValue =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** spell names a refused selection member: a primitive by its value, an object by its type. */
const spell = (value: unknown): string => {
  if (typeof value === "string") {
    return JSON.stringify(value);
  }

  return typeof value === "object" || typeof value === "function"
    ? describeType(value)
    : String(value);
};

/**
 * assertSelectable refuses what a selection level cannot print: a selection that is no object, a
 * key the tree does not have as its own (a name every object inherits included), a member that
 * is neither `true` nor a selection (`false`, `null`, a number), and a sub-selection on a leaf.
 * TypeScript already rejects each for a caller who passes a literal, but `select` can arrive from
 * a config file or an agent, where the types were never checked.
 */
function assertSelectable(
  at: string,
  tree: FieldTree,
  selection: unknown,
): asserts selection is SelectionValue {
  if (!isSelectionValue(selection)) {
    throw new MountayaSelectionError(
      `${at}: a selection is an object naming fields, and this one is ${spell(selection)}; valid fields are: ${Object.keys(tree).join(", ")}`,
    );
  }

  for (const [name, picked] of Object.entries(selection)) {
    if (picked === undefined) {
      continue;
    }

    const node = Object.hasOwn(tree, name) ? tree[name] : undefined;
    if (node === undefined) {
      throw new MountayaSelectionError(
        `${at}: unknown field "${name}"; valid fields are: ${Object.keys(tree).join(", ")}`,
      );
    }

    if (picked !== true && !isSelectionValue(picked)) {
      throw new MountayaSelectionError(
        `${at}.${name} is ${spell(picked)}; select a field with true, or leave it out.`,
      );
    }

    if (node === true && picked !== true) {
      throw new MountayaSelectionError(
        `${at}: field "${name}" is a leaf and takes no sub-selection`,
      );
    }
  }
}

/**
 * exportName upper-cases an operation name for the document's operation name. Cosmetic,
 * and it matches the generator so the two printers agree byte for byte.
 */
const exportName = (name: string): string =>
  name === "" ? name : `${name[0]?.toUpperCase() ?? ""}${name.slice(1)}`;
